/**
 * Angle snapping for free-standing run points.
 *
 * When the user draws a peninsula/island point, snap the segment
 * `prev -> raw` to a multiple of `stepDeg` from a reference direction (the
 * previous leg, the wall it leaves, or the nearest cabinet run) — but only
 * when the freehand angle is already within `toleranceDeg` of one.
 * Beyond the tolerance the raw point is returned unchanged (freehand).
 *
 * Pure 2D ({x,z}) geometry — no wall/run lookups here; the caller resolves the
 * reference direction (see web-app utils/cabinetRunSnap).
 */

/** Wrap an angle (radians) into [-PI, PI]. */
function wrapPi(a) {
  let x = a;
  while (x > Math.PI) x -= 2 * Math.PI;
  while (x < -Math.PI) x += 2 * Math.PI;
  return x;
}

/**
 * Snap `raw` so the segment from `prev` runs at a multiple of `stepDeg` from
 * `refDir`, preserving the segment's length. Returns `raw` unchanged when
 * there is no reference, the segment is degenerate, or the freehand angle is
 * more than `toleranceDeg` from every candidate direction.
 *
 * The defaults are the original square snap (parallel/perpendicular, 10°).
 * The draw tool passes 45° steps within 5° (`bendGeometry.BEND_SNAP_DEG`) —
 * design 2026-11-03.
 *
 * @param {{x:number,z:number}} prev  segment start (the previous vertex)
 * @param {{x:number,z:number}} raw   freehand point (cursor / click)
 * @param {{x:number,z:number}|null} refDir  reference direction (need not be unit)
 * @param {number} [toleranceDeg=10]
 * @param {number} [stepDeg=90]  candidate spacing; must divide 360
 * @returns {{x:number,z:number}}
 */
function snapPointToReference(
  prev,
  raw,
  refDir,
  toleranceDeg = 10,
  stepDeg = 90
) {
  if (!refDir) return raw;
  const refLen = Math.hypot(refDir.x, refDir.z);
  if (refLen < 1e-9) return raw;

  const dx = raw.x - prev.x;
  const dz = raw.z - prev.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-9) return raw;

  const rawAngle = Math.atan2(dz, dx);
  const refAngle = Math.atan2(refDir.z, refDir.x);

  // Every `stepDeg` from the reference, all the way round.
  const step = (stepDeg * Math.PI) / 180;
  const count = Math.round(360 / stepDeg);
  let best = rawAngle;
  let bestDelta = Infinity;
  for (let k = 0; k < count; k += 1) {
    const cand = refAngle + k * step;
    const delta = Math.abs(wrapPi(rawAngle - cand));
    if (delta < bestDelta) {
      bestDelta = delta;
      best = cand;
    }
  }

  if (bestDelta > (toleranceDeg * Math.PI) / 180) return raw;
  return { x: prev.x + len * Math.cos(best), z: prev.z + len * Math.sin(best) };
}

module.exports = { snapPointToReference, wrapPi };
